/**
 * Step 1 - Upload do arquivo CSV (importação de CLIENTES).
 *
 * Recebe arquivo via input/drag, valida tamanho/limites e devolve
 * headers + rows ao orquestrador. Botão para baixar CSV modelo.
 *
 * RELEASE:
 *   2026-08-23 v1.1.0 - Texto de ajuda cita os campos opcionais do modelo completo.
 *   2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */


import { Upload, Download, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { downloadSampleCustomersCsv } from '../lib/sample-csv';

interface Step1UploadCustomersProps {
  jobName: string;
  onJobNameChange: (v: string) => void;
  onFileSelected: (file: File) => void;
}

export function Step1UploadCustomers({
  jobName,
  onJobNameChange,
  onFileSelected,
}: Step1UploadCustomersProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Nome da importação</Label>
        <Input
          value={jobName}
          onChange={(e) => onJobNameChange(e.target.value)}
          placeholder="Ex.: Carteira PJ Outubro"
          maxLength={120}
        />
      </div>

      <label
        htmlFor="csv-upload-customers"
        className="block border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:bg-muted/50 transition"
      >
        <Upload className="mx-auto h-10 w-10 mb-3 text-muted-foreground" />
        <p className="font-medium">Clique para selecionar o CSV</p>
        <p className="text-xs text-muted-foreground mt-1">
          Máx. 50.000 linhas / 10 MB
        </p>
        <input
          id="csv-upload-customers"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onFileSelected(e.target.files[0])}
        />
      </label>

      <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-4">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">Não sabe por onde começar?</p>
          <p className="text-xs text-muted-foreground">
            Baixe um CSV modelo com cabeçalho e 3 linhas de exemplo (PF e PJ).
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => downloadSampleCustomersCsv()}
        >
          <Download className="h-4 w-4 mr-2" />
          Baixar CSV modelo
        </Button>
      </div>

      <div className="flex gap-2 rounded-md border border-blue-300/60 bg-blue-50 p-3 text-blue-900 dark:border-blue-400/30 dark:bg-blue-950/40 dark:text-blue-100">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="text-xs space-y-1">
          <p className="font-medium">Campos obrigatórios em cada linha:</p>
          <ul className="list-disc pl-5">
            <li><strong>nome</strong> (completo do cliente ou responsável)</li>
            <li><strong>telefone</strong> (com DDD, formato livre)</li>
            <li><strong>cpf</strong> <em>ou</em> <strong>cnpj</strong> (pelo menos um deles)</li>
          </ul>
          <p className="opacity-90">
            Linhas sem esses dados serão marcadas como inválidas na etapa de validação
            e poderão ser exportadas como CSV de erros para correção offline.
          </p>
          <p className="opacity-90">
            <strong>Opcionais suportados:</strong> razao_social, nome_fantasia, email,
            data_nascimento, cep, logradouro, numero, complemento, bairro, cidade,
            estado e observacao.
          </p>

        </div>
      </div>
    </div>
  );
}
